/**
 * useSkills - Skills API logic hook
 * Tum API cagrilari burada, component'lar sadece UI
 */
import axios from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { API_URL } from '../../../../config/api'

export interface Skill {
  id: number
  name: string
  description: string
  category: string
  author: string
  version: string
  downloads: number
  rating: number
  is_installed: boolean
  tags: string[]
  icon_name?: string
  color?: string
  featured?: boolean
  is_new?: boolean
  discussion_count?: number
  usage_example?: string
  documentation?: string
  requirements?: string[]
  created_at?: string
  enabled?: boolean
  latest_version?: string
  latest_changelog?: string
  validation_score?: number
  expert_vote_count?: number
  user_vote_count?: number
}

export interface SkillPost {
  id: number
  content: string
  post_type: string
  created_at: string
  like_count: number
  comment_count: number
  display_name?: string
  username?: string
  is_agent?: boolean
  agent_name?: string
  agent_id?: number
  agent?: { name?: string }
}

const authHdr = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`
})

function normalizeStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean)
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return []

    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean)
      }
    } catch {
      return trimmed.split(',').map((item) => item.trim()).filter(Boolean)
    }
  }

  return []
}

function normalizeSkill(raw: any): Skill {
  return {
    ...raw,
    tags: normalizeStringArray(raw?.tags),
    requirements: normalizeStringArray(raw?.requirements),
    validation_score: Number(raw?.validation_score ?? raw?.average_validation_score ?? 0),
    expert_vote_count: Number(raw?.expert_vote_count ?? raw?.expert_validations ?? 0),
    user_vote_count: Number(raw?.user_vote_count ?? 0),
  }
}

export function useSkillsList(category: string, sort: string) {
  const [skills, setSkills] = useState<Skill[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSkills = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, string> = { sort }
      if (category !== 'All') { params.category = category }
      const res = await axios.get(`${API_URL}/api/skills`, {
        headers: authHdr(), params
      })
      setSkills(((res.data as { skills: Skill[] }).skills ?? []).map(normalizeSkill))
    } catch {
      setError('Failed to load skills')
      setSkills([])
    } finally {
      setLoading(false)
    }
  }, [category, sort])

  useEffect(() => { void fetchSkills() }, [fetchSkills])

  const install = async (id: number): Promise<boolean> => {
    try {
      await axios.post(`${API_URL}/api/aicloud/skills/${id}/install`, {}, { headers: authHdr() })
      setSkills(prev => prev.map(s => s.id === id ? { ...s, is_installed: true } : s))
      return true
    } catch {
      return false
    }
  }

  return { skills, loading, error, install, refetch: fetchSkills }
}

export function useSkillDetail(id: string | undefined) {
  const [skill, setSkill] = useState<Skill | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) { return }

    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const r = await axios.get<{ skill: Skill; summary?: { average_score?: number; expert_validations?: number; total_validations?: number } }>(`${API_URL}/api/skills/${id}`, { headers: authHdr() })
        setSkill(normalizeSkill({
          ...r.data.skill,
          validation_score: Number(r.data.summary?.average_score ?? 0),
          expert_vote_count: Number(r.data.summary?.expert_validations ?? 0),
        }))
      } catch (e) {
        const err = e as { response?: { data?: { detail?: string } } }
        setError(err.response?.data?.detail ?? 'Failed to load skill')
      } finally {
        setLoading(false)
      }
    })()
  }, [id])

  const install = async (): Promise<boolean> => {
    if (!skill) { return false }
    try {
      await axios.post(`${API_URL}/api/aicloud/skills/${skill.id}/install`, {}, { headers: authHdr() })
      setSkill(prev => prev ? { ...prev, is_installed: true, enabled: true } : prev)
      return true
    } catch { return false }
  }

  const toggle = async (): Promise<boolean> => {
    if (!skill) { return false }
    try {
      const ep = skill.enabled
        ? `/api/aicloud/skills/${skill.id}/disable`
        : `/api/aicloud/skills/${skill.id}/enable`
      await axios.post(`${API_URL}${ep}`, {}, { headers: authHdr() })
      setSkill(prev => prev ? { ...prev, enabled: !prev.enabled } : prev)
      return true
    } catch { return false }
  }

  return { skill, loading, error, install, toggle }
}

export function useSkillPosts(skillId: string | undefined, active: boolean) {
  const [posts, setPosts] = useState<SkillPost[]>([])
  const [loading, setLoading] = useState(false)

  const loadPosts = useCallback(async () => {
    if (!skillId) { return }
    setLoading(true)
    try {
      const res = await axios.get(`${API_URL}/api/social/posts`, {
        headers: authHdr(),
        params: { skill_id: Number(skillId), limit: 50 }
      })
      const raw = (res.data as { posts: SkillPost[] }).posts ?? []
      const mapped = raw.map((p: SkillPost) => ({
        ...p,
        is_agent: !!p.agent_id,
        agent_name: p.agent?.name ?? undefined
      }))
      setPosts(mapped)
    } catch {
      setPosts([])
    } finally {
      setLoading(false)
    }
  }, [skillId])

  useEffect(() => {
    if (active) { void loadPosts() }
  }, [active, loadPosts])

  const createPost = async (content: string, userId: number): Promise<boolean> => {
    if (!skillId) { return false }
    try {
      await axios.post(
        `${API_URL}/api/social/posts`,
        {
          content,
          post_type: 'skill_discussion',
          aicloud_skill_id: Number(skillId),
          skill_tags: [],
          visibility: 'public',
          user_id: userId
        },
        { headers: authHdr() }
      )
      void loadPosts()
      return true
    } catch { return false }
  }

  return { posts, loading, createPost, reload: loadPosts }
}

export interface VersionEntry {
  id: number
  skill_id: number
  version: string
  date: string
  changelog: string
  summary?: string
  code_snapshot?: string
  synthesis_reason?: string
  created_at?: string
  validation_count?: number
  expert_approvals?: number
  average_validation_score?: number
  issues_fixed: string[]
}

export function useSkillVersions(skillId: string | undefined) {
  const [versions, setVersions] = useState<VersionEntry[]>([])
  const [currentChangelog, setCurrentChangelog] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!skillId) { return }

    void (async () => {
      setLoading(true)
      try {
        const r = await axios.get(`${API_URL}/api/skills/${skillId}/versions`)
        const d = r.data as { version_history?: VersionEntry[]; versions?: VersionEntry[]; changelog?: string }
        const sourceVersions = d.version_history ?? d.versions ?? []
        setVersions(sourceVersions.map((entry: any) => ({
          id: entry.id,
          skill_id: entry.skill_id,
          version: entry.version ?? entry.version_number ?? '',
          date: entry.date ?? entry.created_at ?? '',
          changelog: entry.changelog ?? entry.improvement_description ?? '',
          summary: entry.summary,
          code_snapshot: entry.code_snapshot,
          synthesis_reason: entry.synthesis_reason,
          created_at: entry.created_at,
          validation_count: entry.validation_count,
          expert_approvals: entry.expert_approvals,
          average_validation_score: entry.average_validation_score,
          issues_fixed: entry.issues_fixed ?? [],
        })))
        setCurrentChangelog(d.changelog ?? '')
      } catch {
        // Hata durumunda sessizce devam et
      } finally {
        setLoading(false)
      }
    })()
  }, [skillId])

  return { versions, currentChangelog, loading }
}
